<template>
    <div ref="observedElement" class="flex flex-col p-2 flex-1">
      <div :class="[ 'px-1 text-sm text-left prose max-w-none', item.id === selected || !isRead ? '' : 'text-white' ]">
        <span
          v-for="(chunk, idx) in linkify(item.content)"
          :key="idx"
        >
          <Link
            v-if="chunk.type === 'link'"
            :href="chunk.href"
            :class="[ 'underline hover:no-underline', item.id === selected || !isRead ? '' : 'text-white' ]"
            target="_blank"
          >
            <span v-html="formatChunk(chunk.content)" />
          </Link>
          <span v-else>
            <span v-html="formatChunk(chunk.content)" />
          </span>
        </span>
        {{ (!item.content && item?.images?.length ? '' : ' ') }}
        <div v-if="replyUrl" class="text-right">
          <Link

            :href="replyUrl"
            :class="[ 'no-underline hover:underline italic text-xs mr-2', item.id === selected || !isRead ? '' : 'text-white' ]"
          >
            {{ $t('text-reply') }}
          </Link>
        </div>
      </div>
      <MessageImages v-if="item?.images?.length" :item="item" />
      <div v-if="item.insertedAt" class="flex items-center justify-start text-2xs italic whitespace-nowrap px-1 mt-1 text-right">
        <div :class="item.id === selected || !isRead ? 'text-gray-500' : 'text-gray-300'">{{ $luxon.fromISO(item.insertedAt).setLocale(routeLang.code.value).toRelative() }}</div>
        <button
          v-if="showTranslationAction"
          type="button"
          :disabled="translationLoading"
          :class="[ 'ml-2 underline hover:no-underline disabled:no-underline disabled:opacity-60 text-sm font-bold', item.id === selected || !isRead ? 'text-gray-500' : 'text-gray-300' ]"
          @click="handleTranslationClick"
        >
          {{ isShowingTranslation ? $t('text-show-original') : $t('text-show-translation') }}
        </button>
        <div v-if="position === 'right'" v-tippy="$t('text-is-read')" class="flex items-center justify-center">
          <CheckIcon v-if="item.isRead" :class="[ 'w-3 h-3 ml-2', item.id === selected ? 'text-accent-dark' : 'text-accent-light' ]" :stroke-width="4" />
        </div>
      </div>
    </div>
</template>

<script setup>
import _ from 'lodash-es';
import { useAuthStore } from '@stores/auth';
import { useCustomerStore } from '@stores/customer';
import { CHAT_MESSAGE_TYPE } from "@utils/constants";
import MessageImages from "@components/chat/message-images";
import Link from "@components/ui/link/link";
import CheckIcon from "@components/icons/check-icon";

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  selected: {
    type: Number,
    default: null
  },
  isRead: {
    type: Boolean,
    default: true
  },
  position: {
    type: String,
    required: true
  },
  parentId: {
    type: Number,
    required: true
  },
  type: {
    type: String,
    required: true
  },
  replyUrl: {
    type: String,
    default: null
  }
});

const { $luxon } = useNuxtApp();
const auth = useAuthStore();
const customer = useCustomerStore();
const routeLang = useRouteLang();
const observedElement = ref(null);
const observer = ref(null);
const timeoutId = ref(null);
const translationLoading = ref(false);
const showOriginal = ref(false);

const emit = defineEmits(['read']);

const scheduleRead = () => {
  emit('read');
}

const originalLanguage = computed(() => _.find(props.item?.languages || [], { isTranslated: false }));

const targetLanguageId = computed(() => customer.language?.id);

const targetLanguage = computed(() => _.find(props.item?.languages || [], relation => relation?.language?.id === targetLanguageId.value));

const targetTranslatedLanguage = computed(() => targetLanguage.value?.isTranslated ? targetLanguage.value : null);

const isCsSkLanguagePair = computed(() => {
  const originalCode = originalLanguage.value?.language?.code || originalLanguage.value?.language?.locale;
  const targetCode = customer.language?.code || customer.language?.locale;
  const codes = [originalCode, targetCode].map(code => _.toLower(code).slice(0, 2));

  return codes.includes('cs') && codes.includes('sk');
});

const showTranslationAction = computed(() => {
  return !!(
    originalLanguage.value
    && targetLanguageId.value
    && originalLanguage.value?.language?.id !== targetLanguageId.value
    && !isCsSkLanguagePair.value
  );
});

const isShowingTranslation = computed(() => !!targetTranslatedLanguage.value && !showOriginal.value);

const getTranslation = async () => {
  if (props.type === CHAT_MESSAGE_TYPE.CONTENT) {
    return await contentPostMessageTranslationLoad(props.parentId, props.item.id);
  }

  if (props.type === CHAT_MESSAGE_TYPE.CATALOGUE) {
    return await cataloguePostMessageTranslationLoad(props.parentId, props.item.id);
  }

  if (props.type === CHAT_MESSAGE_TYPE.VARIANT) {
    return await productVariantMessageTranslationLoad(props.parentId, props.item.id);
  }

  if (props.type === CHAT_MESSAGE_TYPE.CART) {
    return await cartMessageTranslationLoad(props.parentId, props.item.id);
  }

  if (props.type === CHAT_MESSAGE_TYPE.SUPPLIER) {
    return await supplierMessageTranslationLoad(props.parentId, props.item.id);
  }

  return null;
}

const setMessageContent = (content, isOriginal) => {
  props.item.content = content;
  showOriginal.value = isOriginal;
}

const handleTranslationClick = async () => {
  if (translationLoading.value || !showTranslationAction.value) {
    return;
  }

  if (isShowingTranslation.value) {
    setMessageContent(originalLanguage.value.content, true);
    return;
  }

  if (targetTranslatedLanguage.value) {
    setMessageContent(targetTranslatedLanguage.value.content, false);
    return;
  }

  translationLoading.value = true;
  const data = await getTranslation();
  translationLoading.value = false;

  if (!data?.languages?.length) {
    return;
  }

  props.item.languages = data.languages;
  const translated = _.find(props.item.languages, relation => relation?.language?.id === targetLanguageId.value);
  if (translated?.content) {
    setMessageContent(translated.content, false);
  }
}

const emojiRanges = [[8596,8601],[8617,8618],[8986,8987],[9000,9000],[9167,9167],[9193,9203],[9208,9210],[9410,9410],[9642,9643],[9654,9654],[9664,9664],[9723,9726],[9728,9732],[9742,9742],[9745,9745],[9748,9749],[9752,9752],[9757,9757],[9760,9760],[9762,9763],[9766,9766],[9770,9770],[9774,9775],[9784,9786],[9792,9792],[9794,9794],[9800,9811],[9824,9824],[9827,9827],[9829,9830],[9832,9832],[9851,9851],[9855,9855],[9874,9879],[9881,9881],[9883,9884],[9888,9889],[9898,9899],[9904,9905],[9917,9918],[9924,9925],[9928,9928],[9934,9935],[9937,9937],[9939,9940],[9961,9962],[9968,9973],[9975,9978],[9981,9981],[9986,9986],[9989,9989],[9992,9997],[9999,9999],[10002,10002],[10004,10004],[10006,10006],[10013,10013],[10017,10017],[10024,10024],[10035,10036],[10052,10052],[10055,10055],[10060,10060],[10062,10062],[10067,10069],[10071,10071],[10083,10084],[10133,10135],[10145,10145],[10160,10160],[10175,10175],[10548,10549],[11013,11015],[11035,11036],[11088,11088],[11093,11093],[12336,12336],[12349,12349],[12951,12951],[12953,12953],[65039,65039],[126980,126980],[127183,127183],[127344,127345],[127358,127359],[127374,127374],[127377,127386],[127462,127487],[127489,127490],[127514,127514],[127535,127535],[127538,127546],[127568,127569],[127744,127777],[127780,127891],[127894,127895],[127897,127899],[127902,127984],[127987,127989],[127991,127994],[128000,128253],[128255,128317],[128329,128334],[128336,128359],[128367,128368],[128371,128378],[128391,128391],[128394,128397],[128400,128400],[128405,128406],[128420,128421],[128424,128424],[128433,128434],[128444,128444],[128450,128452],[128465,128467],[128476,128478],[128481,128481],[128483,128483],[128488,128488],[128495,128495],[128499,128499],[128506,128591],[128640,128709],[128715,128722],[128736,128741],[128745,128745],[128747,128748],[128752,128752],[128755,128760],[129296,129338],[129340,129342],[129344,129349],[129351,129356],[129360,129387],[129408,129431],[129472,129472],[129488,129510]];

const isEmoji = (codePoint) => {
  return emojiRanges.some(([start, end]) => codePoint >= start && codePoint <= end);
}

const formatChunk = (content) => {
  return _.chain([...content])
    .map(char => {
      const codePoint = char.codePointAt(0);

      if (isEmoji(codePoint)) {
        return `<span class="text-lg">${_.escape(char)}</span>`;
      } else if (codePoint === 10) {
        return "<br>";
      }
      return _.escape(char);
    })
    .join('')
    .value();
}

onMounted(() => {
  if (auth.isLoggedIn && auth.idSupplier) {
    observer.value = new window.IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!props.isRead) {
          if (entry.isIntersecting) {
            timeoutId.value = setTimeout(scheduleRead, 5000);
          } else if (timeoutId.value) {
            clearTimeout(timeoutId.value);
            timeoutId.value = null;
          }
        }
      });
    }, {});

    if (observedElement.value) {
      observer.value.observe(observedElement.value);
    }
  }
});

onUnmounted(() => {
  if (auth.isLoggedIn && auth.idSupplier && observedElement.value) {
    observer.value.unobserve(observedElement.value);
  }
});

</script>
